import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";
import "./CheckoutPage.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const CheckoutPage = () => {
  const { user, token } = useAuth();
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [addressMode, setAddressMode] = useState("registered");
  const [newAddress, setNewAddress] = useState({ street: "", city: "", state: "", pin_code: "" });
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");

  const registeredAddress = {
    street: user?.street || "",
    city: user?.city || "",
    state: user?.state || "",
    pin_code: user?.pin_code || "",
  };

  const hasRegisteredAddress = Object.values(registeredAddress).some(Boolean);

  const deliveryAddress = addressMode === "registered" ? registeredAddress : newAddress;

  const handleChange = (e) => {
    setNewAddress((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePlaceOrder = async () => {
    const addr = deliveryAddress;
    if (!addr.street || !addr.city || !addr.state || !addr.pin_code) {
      setError("Please fill in all address fields.");
      return;
    }
    setError("");
    setPlacing(true);
    try {
      const payload = {
        items: cartItems.map((item) => ({
          product_id: item.id,
          name: item.name,
          image: item.image || "",
          price: item.price,
          quantity: item.quantity,
          subtotal: item.price * item.quantity,
        })),
        total_amount: cartTotal,
        delivery_address: addr,
      };

      const res = await fetch(`${API_URL}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to place order");

      clearCart();
      navigate(`/order-success/${data.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  if (cartItems.length === 0) {
    navigate("/cart");
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>

        <div className="checkout-content">
          {/* Left: order summary + address */}
          <div className="checkout-left">
            {/* Order Summary */}
            <section className="checkout-section">
              <h2 className="checkout-section-title">Order Summary</h2>
              <div className="checkout-items">
                {cartItems.map((item) => (
                  <div className="checkout-item" key={item.id}>
                    <img src={item.image} alt={item.name} className="checkout-item-img" />
                    <div className="checkout-item-info">
                      <p className="checkout-item-name">{item.name}</p>
                      <p className="checkout-item-meta">₹{item.price} × {item.quantity}</p>
                    </div>
                    <p className="checkout-item-subtotal">₹{item.price * item.quantity}</p>
                  </div>
                ))}
              </div>
              <div className="checkout-total-row">
                <span>Grand Total</span>
                <span className="checkout-grand-total">₹{cartTotal}</span>
              </div>
            </section>

            {/* Delivery Address */}
            <section className="checkout-section">
              <h2 className="checkout-section-title">Delivery Address</h2>

              <div className="address-mode-toggle">
                {hasRegisteredAddress && (
                  <label className="address-radio">
                    <input
                      type="radio"
                      value="registered"
                      checked={addressMode === "registered"}
                      onChange={() => setAddressMode("registered")}
                    />
                    Use my registered address
                  </label>
                )}
                <label className="address-radio">
                  <input
                    type="radio"
                    value="new"
                    checked={addressMode === "new"}
                    onChange={() => setAddressMode("new")}
                  />
                  Enter a new address
                </label>
              </div>

              {addressMode === "registered" && hasRegisteredAddress ? (
                <div className="address-display">
                  <p>{registeredAddress.street}</p>
                  <p>{registeredAddress.city}, {registeredAddress.state} – {registeredAddress.pin_code}</p>
                </div>
              ) : (
                <div className="address-form">
                  <input name="street"   placeholder="Street / House No."     value={newAddress.street}   onChange={handleChange} />
                  <div className="address-form-row">
                    <input name="city"     placeholder="City"     value={newAddress.city}     onChange={handleChange} />
                    <input name="state"    placeholder="State"    value={newAddress.state}    onChange={handleChange} />
                  </div>
                  <input name="pin_code" placeholder="PIN Code" value={newAddress.pin_code} onChange={handleChange} />
                </div>
              )}
            </section>

            {error && <p className="checkout-error">{error}</p>}

            <button
              className="checkout-place-btn"
              onClick={handlePlaceOrder}
              disabled={placing}
            >
              {placing ? "Placing Order..." : "Place Order"}
            </button>
          </div>

          {/* Right: price breakdown */}
          <div className="checkout-right">
            <div className="checkout-price-card">
              <h2 className="checkout-section-title">Price Details</h2>
              {cartItems.map((item) => (
                <div className="price-row" key={item.id}>
                  <span>{item.name} × {item.quantity}</span>
                  <span>₹{item.price * item.quantity}</span>
                </div>
              ))}
              <div className="price-divider" />
              <div className="price-row price-row--total">
                <span>Total Amount</span>
                <span>₹{cartTotal}</span>
              </div>
              <p className="checkout-note">Payment options will be available soon.</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
